<template>
  <!-- Plan Change Modal -->
  <div class="modal fade" id="planChangeModal" tabindex="-1" aria-labelledby="planChangeModalLabel" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="planChangeModalLabel">
            <i class="bx bx-sync me-2"></i>
            Change Health Insurance Plan
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading plans...</span>
            </div>
            <p class="mt-3">Loading available plans...</p>
          </div>

          <!-- Main Content -->
          <div v-else>
            <!-- Current Plan Summary -->
            <div class="alert alert-info border-primary mb-4">
              <div class="d-flex align-items-center">
                <i class="bx bx-info-circle fs-2 me-3 text-primary"></i>
                <div class="flex-grow-1">
                  <h6 class="alert-heading mb-2">Current Plan Information</h6>
                  <div class="row">
                    <div class="col-md-6">
                      <p class="mb-1">
                        <strong>Plan:</strong> {{ currentPlan?.plan_name || 'No Plan Selected' }}
                        <span v-if="currentPlan" class="badge ms-2" :class="getPlanTypeBadgeClass(currentPlan.plan_type)">
                          {{ currentPlan.plan_type.toUpperCase() }}
                        </span>
                      </p>
                      <p class="mb-1">
                        <strong>Current Premium:</strong> ETB {{ formatCurrency(currentPremium) }}
                        <span class="text-muted">/ year</span>
                      </p>
                    </div>
                    <div class="col-md-6">
                      <p class="mb-1">
                        <strong>Coverage:</strong> ETB {{ formatCurrency(currentPlan?.annual_coverage_limit || 0) }}
                        <span class="text-muted">annually</span>
                      </p>
                      <p class="mb-0">
                        <strong>Members Covered:</strong> {{ totalBeneficiaries }}
                        <span v-if="currentPlan" class="text-success">
                          ({{ Math.min(totalBeneficiaries - 1, parseInt(currentPlan.max_free_dependents) || 0) }} free dependents)
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plan Selection -->
            <div class="mb-4">
              <h6 class="mb-3">
                <i class="bx bx-list-check me-2"></i>
                Available Plans
              </h6>

              <!-- Plan Cards -->
              <div class="row">
                <div v-for="plan in availablePlans" :key="plan.plan_id" class="col-lg-6 mb-4">
                  <div class="card plan-selection-card h-100"
                       :class="{
                         'border-primary selected-plan': selectedPlan?.plan_id === plan.plan_id,
                         'border-secondary current-plan-card': currentPlan?.plan_id === plan.plan_id
                       }"
                       @click="selectPlan(plan)"
                       style="cursor: pointer;">

                    <!-- Plan Header -->
                    <div class="card-header"
                         :class="{
                           'bg-primary text-white': selectedPlan?.plan_id === plan.plan_id,
                           'bg-secondary text-white': currentPlan?.plan_id === plan.plan_id,
                           'bg-light': selectedPlan?.plan_id !== plan.plan_id && currentPlan?.plan_id !== plan.plan_id
                         }">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 class="mb-1" :class="{
                            'text-white': selectedPlan?.plan_id === plan.plan_id || currentPlan?.plan_id === plan.plan_id,
                            'text-dark': selectedPlan?.plan_id !== plan.plan_id && currentPlan?.plan_id !== plan.plan_id
                          }">
                            {{ plan.plan_name }}
                          </h6>
                          <small :class="{
                            'text-white-75': selectedPlan?.plan_id === plan.plan_id || currentPlan?.plan_id === plan.plan_id,
                            'text-muted': selectedPlan?.plan_id !== plan.plan_id && currentPlan?.plan_id !== plan.plan_id
                          }">
                            {{ plan.plan_description }}
                          </small>
                        </div>
                        <div class="d-flex flex-column align-items-end">
                          <!-- Current Plan Badge -->
                          <span v-if="currentPlan?.plan_id === plan.plan_id" class="badge bg-warning text-dark mb-1">
                            <i class="bx bx-star me-1"></i>
                            Current Plan
                          </span>
                          <!-- Selected Badge -->
                          <span v-else-if="selectedPlan?.plan_id === plan.plan_id" class="badge bg-success text-white mb-1">
                            <i class="bx bx-check-circle me-1"></i>
                            Selected
                          </span>
                          <!-- Plan Type Badge -->
                          <span class="badge" :class="getPlanTypeBadgeClass(plan.plan_type)">
                            {{ plan.plan_type.toUpperCase() }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Plan Body -->
                    <div class="card-body">
                      <!-- Pricing Information -->
                      <div class="row mb-3">
                        <div class="col-6 text-center">
                          <div class="pricing-display">
                            <h5 class="text-primary mb-1">ETB {{ formatCurrency(plan.primary_annual_amount) }}</h5>
                            <small class="text-muted">Primary Member</small>
                          </div>
                        </div>
                        <div class="col-6 text-center">
                          <div class="pricing-display">
                            <h6 class="mb-1">ETB {{ formatCurrency(plan.dependent_annual_amount) }}</h6>
                            <small class="text-muted">Each Dependent</small>
                          </div>
                        </div>
                      </div>

                      <!-- Coverage Summary -->
                      <div class="coverage-summary mb-3">
                        <div class="row">
                          <div class="col-sm-6 mb-2">
                            <div class="coverage-item">
                              <i class="bx bx-calendar text-primary me-2"></i>
                              <span class="small">
                                Annual: <strong>ETB {{ formatCurrency(plan.annual_coverage_limit) }}</strong>
                              </span>
                            </div>
                          </div>
                          <div class="col-sm-6 mb-2">
                            <div class="coverage-item">
                              <i class="bx bx-plus-medical text-primary me-2"></i>
                              <span class="small">
                                Per Visit: <strong>ETB {{ formatCurrency(plan.per_visit_limit) }}</strong>
                              </span>
                            </div>
                          </div>
                          <div class="col-sm-6 mb-2">
                            <div class="coverage-item">
                              <i class="bx bx-group text-success me-2"></i>
                              <span class="small">
                                <strong>{{ plan.max_free_dependents }}</strong> Free Dependents
                              </span>
                            </div>
                          </div>
                          <div class="col-sm-6 mb-2">
                            <div class="coverage-item">
                              <i class="bx bx-list-check text-info me-2"></i>
                              <span class="small">
                                <strong>{{ plan.benefits?.services?.length || 0 }}</strong> Services Covered
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Your Premium Calculation -->
                      <div class="premium-calculation bg-light p-3 rounded">
                        <h6 class="mb-2 text-primary">Your Premium with This Plan:</h6>
                        <div class="d-flex justify-content-between align-items-center">
                          <div>
                            <div class="premium-breakdown">
                              <div class="small">
                                Primary: ETB {{ formatCurrency(plan.primary_annual_amount) }}
                              </div>
                              <div v-if="getPaidDependents(plan) > 0" class="small">
                                {{ getPaidDependents(plan) }} Paid Dependents: ETB {{ formatCurrency(getPaidDependents(plan) * parseFloat(plan.dependent_annual_amount)) }}
                              </div>
                              <div v-if="getFreeDependents(plan) > 0" class="small text-success">
                                {{ getFreeDependents(plan) }} Free Dependents: ETB 0
                              </div>
                            </div>
                          </div>
                          <div class="text-end">
                            <h5 class="text-primary mb-0">ETB {{ formatCurrency(calculatePremiumForPlan(plan)) }}</h5>
                            <small class="text-muted">Total Annual</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plan Comparison -->
            <div v-if="selectedPlan && selectedPlan.plan_id !== currentPlan?.plan_id" class="mb-4">
              <h6 class="mb-3">
                <i class="bx bx-compare me-2"></i>
                Plan Comparison
              </h6>

              <div class="card border-warning">
                <div class="card-header bg-warning">
                  <h6 class="mb-0 text-dark">
                    <i class="bx bx-transfer me-2"></i>
                    Changing from {{ currentPlan?.plan_name }} to {{ selectedPlan.plan_name }}
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <!-- Premium Comparison -->
                    <div class="col-md-4 mb-3">
                      <div class="comparison-item">
                        <h6 class="text-primary mb-2">Premium Impact</h6>
                        <div class="premium-change">
                          <div class="d-flex justify-content-between">
                            <span>Current:</span>
                            <strong>ETB {{ formatCurrency(currentPremium) }}</strong>
                          </div>
                          <div class="d-flex justify-content-between">
                            <span>New:</span>
                            <strong>ETB {{ formatCurrency(calculatePremiumForPlan(selectedPlan)) }}</strong>
                          </div>
                          <hr class="my-2">
                          <div class="d-flex justify-content-between">
                            <span><strong>Difference:</strong></span>
                            <strong :class="premiumDifference >= 0 ? 'text-danger' : 'text-success'">
                              {{ premiumDifference >= 0 ? '+' : '' }}ETB {{ formatCurrency(Math.abs(premiumDifference)) }}
                            </strong>
                          </div>
                          <small v-if="premiumDifference > 0" class="text-danger">
                            <i class="bx bx-trending-up me-1"></i>
                            Premium will increase
                          </small>
                          <small v-else-if="premiumDifference < 0" class="text-success">
                            <i class="bx bx-trending-down me-1"></i>
                            Premium will decrease
                          </small>
                          <small v-else class="text-muted">
                            <i class="bx bx-minus me-1"></i>
                            No premium change
                          </small>
                        </div>
                      </div>
                    </div>

                    <!-- Coverage Comparison -->
                    <div class="col-md-4 mb-3">
                      <div class="comparison-item">
                        <h6 class="text-primary mb-2">Coverage Changes</h6>
                        <div class="coverage-changes">
                          <div class="change-item mb-2">
                            <small class="text-muted">Annual Coverage:</small>
                            <div class="d-flex justify-content-between">
                              <span>{{ formatCurrency(currentPlan?.annual_coverage_limit || 0) }}</span>
                              <i class="bx bx-right-arrow-alt mx-2"></i>
                              <span class="text-primary">{{ formatCurrency(selectedPlan.annual_coverage_limit) }}</span>
                            </div>
                          </div>
                          <div class="change-item mb-2">
                            <small class="text-muted">Per Visit Limit:</small>
                            <div class="d-flex justify-content-between">
                              <span>{{ formatCurrency(currentPlan?.per_visit_limit || 0) }}</span>
                              <i class="bx bx-right-arrow-alt mx-2"></i>
                              <span class="text-primary">{{ formatCurrency(selectedPlan.per_visit_limit) }}</span>
                            </div>
                          </div>
                          <div class="change-item">
                            <small class="text-muted">Free Dependents:</small>
                            <div class="d-flex justify-content-between">
                              <span>{{ currentPlan?.max_free_dependents || 0 }}</span>
                              <i class="bx bx-right-arrow-alt mx-2"></i>
                              <span class="text-success">{{ selectedPlan.max_free_dependents }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Services Impact -->
                    <div class="col-md-4 mb-3">
                      <div class="comparison-item">
                        <h6 class="text-primary mb-2">Services Impact</h6>
                        <div class="services-impact">
                          <div class="impact-item mb-2">
                            <small class="text-muted">Services Covered:</small>
                            <div class="d-flex justify-content-between">
                              <span>{{ currentPlan?.benefits?.services?.length || 0 }}</span>
                              <i class="bx bx-right-arrow-alt mx-2"></i>
                              <span class="text-info">{{ selectedPlan.benefits?.services?.length || 0 }}</span>
                            </div>
                          </div>

                          <!-- Service changes would go here if we had detailed comparison -->
                          <div class="mt-2">
                            <button class="btn btn-outline-info btn-sm" @click="showServiceComparison = !showServiceComparison">
                              <i class="bx bx-list-ul me-1"></i>
                              {{ showServiceComparison ? 'Hide' : 'View' }} Service Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Service Comparison Details -->
                  <div v-if="showServiceComparison" class="service-comparison mt-3 pt-3 border-top">
                    <div class="row">
                      <div class="col-md-6">
                        <h6 class="text-secondary mb-2">Current Plan Services</h6>
                        <div class="service-list">
                          <div v-if="currentPlan?.benefits?.services?.length" class="services-grid">
                            <span v-for="service in currentPlan.benefits.services" :key="service.id"
                                  class="badge bg-light text-dark me-1 mb-1">
                              {{ service.name }}
                            </span>
                          </div>
                          <div v-else class="text-muted small">No services data available</div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <h6 class="text-primary mb-2">New Plan Services</h6>
                        <div class="service-list">
                          <div v-if="selectedPlan.benefits?.services?.length" class="services-grid">
                            <span v-for="service in selectedPlan.benefits.services" :key="service.id"
                                  class="badge bg-primary text-white me-1 mb-1">
                              {{ service.name }}
                            </span>
                          </div>
                          <div v-else class="text-muted small">No services data available</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Change Reason -->
            <div v-if="selectedPlan && selectedPlan.plan_id !== currentPlan?.plan_id" class="mb-4">
              <h6 class="mb-3">
                <i class="bx bx-message-dots me-2"></i>
                Reason for Plan Change
              </h6>
              <div class="form-group">
                <label for="changeReason" class="form-label">Please provide a reason for changing plans:</label>
                <textarea v-model="changeReason"
                          class="form-control"
                          id="changeReason"
                          rows="3"
                          placeholder="e.g., Need better coverage, Cost reduction, Different service requirements..."></textarea>
              </div>
            </div>

            <!-- Alerts -->
            <div v-if="alert.success" class="alert alert-success" role="alert">
              <i class="bx bx-check-circle me-2"></i>
              {{ message }}
            </div>
            <div v-if="alert.failed" class="alert alert-danger" role="alert">
              <i class="bx bx-error-circle me-2"></i>
              {{ message }}
            </div>
            <div v-if="alert.info" class="alert alert-info" role="alert">
              <i class="bx bx-info-circle me-2"></i>
              {{ message }}
            </div>

            <!-- No Plans Available -->
            <div v-if="!loading && availablePlans.length === 0" class="text-center py-5">
              <i class="bx bx-package display-1 text-muted"></i>
              <h5 class="mt-3">No Plans Available</h5>
              <p class="text-muted">There are currently no active plans available for selection.</p>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <div class="d-flex justify-content-between align-items-center w-100">
            <div>
              <span v-if="selectedPlan && selectedPlan.plan_id !== currentPlan?.plan_id" class="text-muted small">
                <i class="bx bx-info-circle me-1"></i>
                Plan changes take effect immediately upon confirmation.
              </span>
            </div>
            <div>
              <button type="button" class="btn btn-secondary me-2" @click="closeModal">
                <i class="bx bx-x me-1"></i>
                Cancel
              </button>
              <button type="button"
                      class="btn btn-primary"
                      @click="confirmPlanChange"
                      :disabled="!selectedPlan || selectedPlan.plan_id === currentPlan?.plan_id || processing || !changeReason.trim()"
                      v-if="selectedPlan && selectedPlan.plan_id !== currentPlan?.plan_id">
                <span v-if="processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bx bx-check me-1"></i>
                {{ processing ? 'Processing...' : 'Confirm Plan Change' }}
              </button>
              <span v-else-if="selectedPlan && selectedPlan.plan_id === currentPlan?.plan_id"
                    class="text-muted">
                This is your current plan
              </span>
              <span v-else class="text-muted">
                Select a different plan to continue
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "PlanChangeModal",
  props: {
    memberId: {
      type: [String, Number],
      required: true
    },
    memberInfo: {
      type: Object,
      default: () => ({})
    },
    currentPlan: {
      type: Object,
      default: null
    },
    totalBeneficiaries: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      // Modal state
      isVisible: false,
      loading: false,
      processing: false,

      // Plan data
      availablePlans: [],
      selectedPlan: null,

      // Form data
      changeReason: '',
      showServiceComparison: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    // Calculate current premium
    currentPremium() {
      if (!this.currentPlan) return 0;

      const primaryRate = parseFloat(this.currentPlan.primary_annual_amount) || 0;
      const dependentRate = parseFloat(this.currentPlan.dependent_annual_amount) || 0;
      const paidDependents = this.getPaidDependents(this.currentPlan);

      return primaryRate + (paidDependents * dependentRate);
    },

    // Calculate premium difference
    premiumDifference() {
      if (!this.selectedPlan) return 0;
      return this.calculatePremiumForPlan(this.selectedPlan) - this.currentPremium;
    }
  },

  methods: {
    // Open modal
    openModal() {
      this.resetModal();
      this.loadAvailablePlans();

      // Show modal using Bootstrap
      const modalElement = document.getElementById('planChangeModal');
      if (modalElement && typeof bootstrap !== 'undefined') {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
        this.isVisible = true;
      }
    },

    // Close modal
    closeModal() {
      const modalElement = document.getElementById('planChangeModal');
      if (modalElement && typeof bootstrap !== 'undefined') {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
      this.isVisible = false;
      this.resetModal();
    },

    // Reset modal state
    resetModal() {
      this.selectedPlan = null;
      this.changeReason = '';
      this.showServiceComparison = false;
      this.processing = false;
      this.clearAlerts();
    },

    // Load available plans
    async loadAvailablePlans() {
      this.loading = true;
      this.clearAlerts();

      const data = {
        "function": "getAvailablePlans"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.availablePlans = res.data.data.plans || [];

          // Auto-select current plan if available
          if (this.currentPlan) {
            this.selectedPlan = this.availablePlans.find(p => p.plan_id === this.currentPlan.plan_id) || null;
          }
        } else {
          this.showAlert('failed', 'Failed to load available plans');
        }
      } catch (e) {
        console.error("Failed to load plans:", e);
        this.showAlert('failed', 'Error loading available plans');
      } finally {
        this.loading = false;
      }
    },

    // Select a plan
    selectPlan(plan) {
      this.selectedPlan = plan;
      this.clearAlerts();

      // Auto-populate reason if switching to a different plan type
      if (plan.plan_id !== this.currentPlan?.plan_id && !this.changeReason.trim()) {
        if (this.premiumDifference > 0) {
          this.changeReason = `Upgrading to ${plan.plan_name} for better coverage and benefits.`;
        } else if (this.premiumDifference < 0) {
          this.changeReason = `Switching to ${plan.plan_name} for cost savings while maintaining adequate coverage.`;
        } else {
          this.changeReason = `Changing to ${plan.plan_name} for different service offerings.`;
        }
      }
    },

    // Confirm plan change
    async confirmPlanChange() {
      if (!this.selectedPlan) {
        this.showAlert('failed', 'Please select a plan');
        return;
      }

      if (this.selectedPlan.plan_id === this.currentPlan?.plan_id) {
        this.showAlert('info', 'You have selected your current plan');
        return;
      }

      if (!this.changeReason.trim()) {
        this.showAlert('failed', 'Please provide a reason for the plan change');
        return;
      }

      this.processing = true;
      this.clearAlerts();

      const data = {
        "function": "changeMemberPlan",
        "memberId": this.memberId,
        "newPlanId": this.selectedPlan.plan_id,
        "reason": this.changeReason.trim()
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Plan changed successfully!');

          // Emit success event after a short delay
          setTimeout(() => {
            this.$emit('plan-changed', {
              success: true,
              message: 'Plan changed successfully',
              oldPlan: this.currentPlan,
              newPlan: this.selectedPlan,
              premiumDifference: this.premiumDifference,
              data: res.data.data
            });

            this.closeModal();
          }, 1500);
        } else {
          this.showAlert('failed', res.data.message || 'Failed to change plan');
        }
      } catch (e) {
        console.error("Failed to change plan:", e);
        this.showAlert('failed', e.message || 'Error changing plan');
      } finally {
        this.processing = false;
      }
    },

    // Calculate premium for a specific plan
    calculatePremiumForPlan(plan) {
      if (!plan) return 0;

      const primaryRate = parseFloat(plan.primary_annual_amount) || 0;
      const dependentRate = parseFloat(plan.dependent_annual_amount) || 0;
      const paidDependents = this.getPaidDependents(plan);

      return primaryRate + (paidDependents * dependentRate);
    },

    // Get number of paid dependents for a plan
    getPaidDependents(plan) {
      if (!plan) return 0;

      const totalDependents = Math.max(0, this.totalBeneficiaries - 1); // Exclude primary member
      const maxFree = parseInt(plan.max_free_dependents) || 0;

      return Math.max(0, totalDependents - maxFree);
    },

    // Get number of free dependents for a plan
    getFreeDependents(plan) {
      if (!plan) return 0;

      const totalDependents = Math.max(0, this.totalBeneficiaries - 1); // Exclude primary member
      const maxFree = parseInt(plan.max_free_dependents) || 0;

      return Math.min(totalDependents, maxFree);
    },

    // Get plan type badge class
    getPlanTypeBadgeClass(planType) {
      const typeClasses = {
        'basic': 'bg-info',
        'standard': 'bg-primary',
        'premium': 'bg-success',
        'family': 'bg-warning text-dark'
      };
      return typeClasses[planType?.toLowerCase()] || 'bg-secondary';
    },

    // Format currency
    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    // Alert management
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
      this.message = '';
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 3000);
      }
    }
  },

  // Clean up on component destroy
  beforeUnmount() {
    // Ensure modal is properly closed
    if (this.isVisible) {
      this.closeModal();
    }
  }
};
</script>

<style scoped>
/* Plan Change Modal Styles */

/* Modal Enhancements */
.modal-dialog {
  max-width: 1200px;
}

.modal-header.bg-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%) !important;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer.bg-light {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

/* Plan Selection Cards */
.plan-selection-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-width: 2px;
}

.plan-selection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.plan-selection-card.selected-plan {
  border-color: #2c5aa0 !important;
  box-shadow: 0 0 0 0.2rem rgba(44, 90, 160, 0.25);
}

.plan-selection-card.current-plan-card {
  border-color: #6c757d !important;
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.25);
}

/* Card Header Styles */
.plan-selection-card .card-header.bg-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%) !important;
}

.plan-selection-card .card-header.bg-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #8d969f 100%) !important;
}

.plan-selection-card .card-header.bg-light {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

/* Pricing Display */
.pricing-display {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.pricing-display:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
  border-color: #2c5aa0;
}

/* Coverage Summary */
.coverage-summary {
  background-color: #fefefe;
  border-radius: 0.375rem;
  padding: 0.75rem;
  border: 1px solid #f1f3f4;
}

.coverage-item {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
}

.coverage-item i {
  flex-shrink: 0;
  font-size: 1rem;
}

/* Premium Calculation */
.premium-calculation {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.premium-calculation:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
  border-color: #2c5aa0;
}

.premium-breakdown {
  font-size: 0.875rem;
}

.premium-breakdown .small {
  line-height: 1.2;
  margin-bottom: 0.125rem;
}

/* Plan Comparison */
.comparison-item {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  height: 100%;
}

.premium-change,
.coverage-changes,
.services-impact {
  font-size: 0.875rem;
}

.change-item {
  background-color: #fefefe;
  padding: 0.5rem;
  border-radius: 0.25rem;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}

.change-item:hover {
  border-left-color: #2c5aa0;
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
}

.impact-item {
  padding: 0.25rem 0;
}

/* Service Comparison */
.service-comparison {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 0.5rem;
  padding: 1rem;
}

.service-list {
  max-height: 150px;
  overflow-y: auto;
}

.services-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.services-grid .badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.services-grid .badge:hover {
  transform: scale(1.05);
}

/* Badge Styles */
.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.badge.bg-info {
  background-color: #0dcaf0 !important;
  color: #000 !important;
}

.badge.bg-primary {
  background-color: #2c5aa0 !important;
  color: white !important;
}

.badge.bg-success {
  background-color: #198754 !important;
  color: white !important;
}

.badge.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
  color: white !important;
}

.badge.bg-light {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  border: 1px solid #dee2e6;
}

/* Alert Styles */
.alert {
  border-radius: 0.5rem;
  border-width: 1px;
  border-left-width: 4px;
}

.alert-success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-left-color: #28a745;
}

.alert-danger {
  background: linear-gradient(135deg, #f8d7da 0%, #f1b0b7 100%);
  border-left-color: #dc3545;
}

.alert-info {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  border-left-color: #2c5aa0;
}

.alert-warning {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-left-color: #ffc107;
}

/* Form Controls */
.form-control {
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #2c5aa0;
  box-shadow: 0 0 0 0.2rem rgba(44, 90, 160, 0.25);
}

/* Button Styles */
.btn {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: #2c5aa0;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #1e3f70 0%, #357abd 100%);
  border-color: #1e3f70;
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #8d969f 100%);
  border-color: #6c757d;
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #545b62 0%, #6d7580 100%);
  border-color: #545b62;
}

.btn-outline-info {
  color: #17a2b8;
  border-color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Spinner */
.spinner-border {
  width: 2rem;
  height: 2rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Text Utilities */
.text-white-75 {
  color: rgba(255, 255, 255, 0.75) !important;
}

.text-primary {
  color: #2c5aa0 !important;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

/* Close Button Enhancement */
.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

/* Scrollbar Styling */
.modal-body::-webkit-scrollbar,
.service-list::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track,
.service-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb,
.service-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.3s ease;
}

.modal-body::-webkit-scrollbar-thumb:hover,
.service-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Animation Enhancements */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.plan-selection-card {
  animation: slideIn 0.3s ease-out;
}

.comparison-item {
  animation: slideIn 0.4s ease-out;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .modal-dialog {
    max-width: 95%;
    margin: 1rem;
  }
}

@media (max-width: 992px) {
  .modal-dialog {
    max-width: 90%;
  }

  .premium-calculation .d-flex {
    flex-direction: column;
    text-align: center;
  }

  .premium-calculation .text-end {
    text-align: center !important;
    margin-top: 1rem;
  }

  .change-item .d-flex {
    flex-direction: column;
    text-align: center;
    gap: 0.25rem;
  }
}

@media (max-width: 768px) {
  .modal-body {
    max-height: 60vh;
    padding: 1rem;
  }

  .plan-selection-card {
    margin-bottom: 1rem;
  }

  .pricing-display {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .coverage-summary,
  .premium-calculation {
    padding: 0.5rem;
  }

  .comparison-item {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 1rem;
  }

  .modal-footer .d-flex {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.5rem;
  }

  .services-grid {
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 0.5rem;
    max-width: 95%;
  }

  .card-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .card-header .d-flex > div:last-child {
    margin-top: 0.5rem;
    align-self: flex-end;
  }

  .coverage-item {
    font-size: 0.8rem;
  }

  .badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.3rem;
  }

  .premium-breakdown {
    font-size: 0.8rem;
  }

  .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }
}

/* Print Styles */
@media print {
  .modal {
    position: static !important;
    display: block !important;
  }

  .modal-dialog {
    max-width: none !important;
    margin: 0 !important;
  }

  .modal-header,
  .modal-footer,
  .btn {
    display: none !important;
  }

  .plan-selection-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .plan-selection-card,
  .comparison-item,
  .pricing-display,
  .premium-calculation {
    border-width: 2px;
  }

  .badge {
    border: 1px solid;
  }

  .btn {
    border-width: 2px;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .plan-selection-card,
  .comparison-item,
  .btn,
  .badge,
  .pricing-display,
  .premium-calculation {
    transition: none;
  }

  .plan-selection-card:hover,
  .btn:hover {
    transform: none;
  }
}
</style>